import { useSyncExternalStore } from "react";

// Same dependency-free global-store pattern as usePaywallDialog: any file
// handler can ask for a PDF password without being nested under a
// provider; the dialog itself is mounted once, globally, in Layout.tsx.
// Requests queue, so dropping several locked files shows one prompt at a
// time instead of stacking dialogs.
export interface PdfPasswordRequest {
  id: number;
  fileName: string;
  previousAttemptWasWrong: boolean;
  /** A one-file tool: "Skip this file" would read oddly, so the button says Cancel. */
  singleFile: boolean;
  resolve: (password: string | null) => void;
}

let current: PdfPasswordRequest | null = null;
let nextId = 1;
const queue: PdfPasswordRequest[] = [];
const listeners = new Set<() => void>();

function notify() {
  Array.from(listeners).forEach((l) => l());
}

function pump() {
  if (!current && queue.length > 0) current = queue.shift()!;
  notify();
}

export function requestPdfPassword(fileName: string, previousAttemptWasWrong = false, singleFile = false): Promise<string | null> {
  return new Promise((resolve) => {
    queue.push({ id: nextId++, fileName, previousAttemptWasWrong, singleFile, resolve });
    pump();
  });
}

/** Pass the entered password, or null to skip the file. */
export function answerPdfPassword(password: string | null) {
  const answered = current;
  current = null;
  answered?.resolve(password);
  pump();
}

export function useCurrentPdfPasswordRequest() {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => current
  );
}
